import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { HasViewAccessRequestInterface, ViewAccessApiService } from '@vendasta/platform-users';
import { Views, AccessService, Feature } from './constants';
import { AccountGroupService } from '../../account-group';
import { WhitelabelService } from '@galaxy/partner';
import { AccountGroupApiService } from '@vendasta/account-group';

@Injectable({ providedIn: 'root' })
export class StubAccessService implements AccessService {
  features$: Observable<string[]>;

  constructor(
    private readonly viewAccessService: ViewAccessApiService,
    private readonly accountGroupService: AccountGroupService,
    private readonly accountGroupApiService: AccountGroupApiService,
    private readonly whitelabelService: WhitelabelService,
  ) {
    const parentNamespace$ = accountGroupService.currentAccountGroupId$.pipe(
      switchMap((accountGroupId) =>
        this.accountGroupApiService.getMulti({
          accountGroupIds: [accountGroupId],
          projectionFilter: { accountGroupExternalIdentifiers: true },
        }),
      ),
      map((response) => response.accountGroups[0]?.accountGroup?.accountGroupExternalIdentifiers?.partnerId),
    );
    this.features$ = parentNamespace$.pipe(
      switchMap((pid) => this.whitelabelService.getConfiguration(pid)),
      map((config) => config.enabledFeatures),
      shareReplay(1),
    );
  }

  // note: this should not to be confused with `vconfig/vconfig.service.ts`
  // this is for value based pricing feature access checks
  hasAccessToFeature(feature: Feature): Observable<boolean> {
    return this.features$.pipe(
      map((features) => {
        return Boolean(features && features.indexOf(feature) >= 0);
      }),
      distinctUntilChanged(),
    );
  }

  // note: this should not to be confused with `vconfig/vconfig.service.ts`
  // this is for value based pricing feature access checks
  hasAccessToFeatures(features: Feature[]): Observable<boolean> {
    if (!features) {
      return of(true);
    }
    return this.features$.pipe(
      map((partnerFeatures) => {
        return features.every((feature) => Boolean(partnerFeatures && partnerFeatures.indexOf(feature) >= 0));
      }),
      distinctUntilChanged(),
    );
  }

  hasAccessToViews(views: Views[]): Observable<Map<Views, boolean>> {
    return this.accountGroupService.currentAccountGroupId$.pipe(
      switchMap((agid) =>
        this.viewAccessService.hasViewAccess({
          partnerId: agid,
          viewIds: views,
        } as HasViewAccessRequestInterface),
      ),
      map((response) => {
        const hasAccess = new Map<Views, boolean>();
        response.access.forEach((view) => {
          const viewId = view.viewId as Views;
          hasAccess.set(viewId, view.hasAccess ?? false);
        });
        return hasAccess;
      }),
    );
  }
}
